import { reduceControlPaused } from '@/lib/controlPause'
import { initialPrivacyScreen, reducePrivacyScreen, type PrivacyScreenState } from '@/lib/privacyScreen'
import { toast } from '@/lib/toast'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ClipboardKind, ConsoleToUi, ControlMessage, DisplayInfo, EndReason, IceServer, InputEvent, SessionState, VideoCodec } from '@/protocol'
import { applyChatLine, mergeChatSeed, type ChatLine } from '@/features/chat/transcript'
import { uiSocket } from '@/lib/ws'

export type { ChatLine } from '@/features/chat/transcript'
import { applyCodecPreferences, fromRtcCandidate, readStats, toRtcCandidate, toRtcIceServers, type RtcStatsSnapshot } from '@/lib/webrtc'
import { mapVideoTransceivers, primaryDisplay, videoTransceiverCount } from '@/lib/displays'
import { RtcFilesChannel } from '@/features/files/channel'
import { transferManager } from '@/features/files/store'
import { CursorStore } from '@/components/RemoteCursor'
import { moveChannel, type FastChannelState } from '@/lib/perf'

export type ViewerPhase = 'idle' | 'connecting' | 'awaiting_approval' | 'connected' | 'ended' | 'error'

export interface ViewerError {
  code: string
  message: string
}

export interface AgentStats {
  display: number
  codec: VideoCodec
  fps: number
  bitrate_kbps: number
  width: number
  height: number
  pipeline_ms: number
  hardware: boolean
  /** encoded picture (smaller than the display when viewport scaling is active) */
  encodedWidth: number
  encodedHeight: number
  captureToEncodedMs: number
  encodeMs: number
  keyframes: number
  framesSkippedIdle: number
}

export interface RemoteClipboardRich {
  kind: ClipboardKind
  names: string[]
  totalBytes: number
  at: number
}

export interface ViewerState {
  phase: ViewerPhase
  sessionId: string | null
  codec: VideoCodec | null
  /** negotiated by the browser (what we asked for) */
  requestedCodec: 'h265' | 'h264' | 'unknown'
  error: ViewerError | null
  endReason: EndReason | null
  displays: DisplayInfo[]
  /** display `select_display` targets (input coordinates refer to it) */
  currentDisplay: number
  /** displays the agent is streaming */
  activeDisplays: number[]
  /** display index → stream */
  streams: Record<number, MediaStream>
  audioAvailable: boolean
  audioEnabled: boolean
  audioStream: MediaStream | null
  /** per display */
  agentStats: Record<number, AgentStats>
  rtcStats: RtcStatsSnapshot | null
  iceState: RTCIceConnectionState | 'new'
  remoteClipboard: string | null
  remoteClipboardRich: RemoteClipboardRich | null
  chat: ChatLine[]
  unreadChat: number
  filesOpen: boolean
  observers: string[]
  /** the person at the device paused remote control (only they can resume) */
  controlPaused: boolean
  /** the agent refused annotations (policy or device-side setting) */
  annotationsDisabled: boolean
  /** the device's own displays are hidden behind a notice (follows the agent's echo, not the button) */
  privacyScreen: PrivacyScreenState
  /** the agent opened the unreliable pointer-move channel */
  fastInput: FastChannelState
}

const initial: ViewerState = {
  phase: 'idle',
  sessionId: null,
  codec: null,
  requestedCodec: 'unknown',
  error: null,
  endReason: null,
  displays: [],
  currentDisplay: 0,
  activeDisplays: [],
  streams: {},
  audioAvailable: false,
  audioEnabled: false,
  audioStream: null,
  agentStats: {},
  rtcStats: null,
  iceState: 'new',
  remoteClipboard: null,
  remoteClipboardRich: null,
  chat: [],
  unreadChat: 0,
  filesOpen: false,
  observers: [],
  controlPaused: false,
  annotationsDisabled: false,
  privacyScreen: initialPrivacyScreen,
  fastInput: 'closed',
}

const CREATE_TIMEOUT_MS = 10_000
const CONNECT_TIMEOUT_MS = 30_000

const ERROR_TEXT: Record<string, string> = {
  device_offline: 'The device is offline.',
  device_busy: 'Someone else is already connected to this device.',
  forbidden: "You don't have connect permission for this device — ask an admin.",
  denied: 'The person at the device declined the request.',
  approval_timeout: 'Nobody answered at the device.',
  agent_error: 'The agent reported an error.',
  connection_failed: 'The connection could not be established. A TURN relay may be required.',
  no_ice_candidates:
    'Your browser gathered no network candidates, so WebRTC cannot connect: a VPN or privacy extension (e.g. Proton VPN, uBlock Origin) has set the WebRTC IP handling policy to "Disable non-proxied UDP". Allow WebRTC for this site, or configure a TURN relay on the console.',
  timeout: 'The console did not respond in time.',
  ws_closed: 'Lost the connection to the console.',
}

export interface ViewerSessionOptions {
  /** Displays known from the device summary before connecting (one transceiver each). */
  knownDisplays: DisplayInfo[]
  /** Whether the device config allows audio (an audio transceiver is added anyway; the agent ignores it otherwise). */
  wantAudio?: boolean
  /** A chat line from the device arrived; `drawerOpen` tells whether the operator is looking at the chat. */
  onChatNotify?: (line: ChatLine, drawerOpen: boolean) => void
}

/**
 * Drives one remote control session: signaling over /ws/ui, the RTCPeerConnection with one
 * video transceiver per display plus audio, the `input`, `control` and `files` data channels,
 * chat and statistics.
 */
export function useViewerSession(deviceId: string, options: ViewerSessionOptions) {
  const [state, setState] = useState<ViewerState>(initial)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const inputRef = useRef<RTCDataChannel | null>(null)
  const fastInputRef = useRef<RTCDataChannel | null>(null)
  const fastStateRef = useRef<FastChannelState>('closed')
  const offerAtRef = useRef(0)
  /** Remote cursor shape/position store shared with the tiles (positions bypass React state). */
  const [cursorStore] = useState(() => new CursorStore())
  const controlRef = useRef<RTCDataChannel | null>(null)
  const filesRef = useRef<RTCDataChannel | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([])
  const remoteSet = useRef(false)
  const unsubscribe = useRef<(() => void) | null>(null)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const statsTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const phaseRef = useRef<ViewerPhase>('idle')
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })
  const chatOpenRef = useRef(false)


  const patch = useCallback((p: Partial<ViewerState> | ((s: ViewerState) => Partial<ViewerState>)) => {
    setState((s) => {
      const next = typeof p === 'function' ? p(s) : p
      if (next.phase) phaseRef.current = next.phase
      return { ...s, ...next }
    })
  }, [])

  /** Apply an incoming chat line (device or operator echo) and notify the page about device lines. */
  const receiveChat = useCallback(
    (from: ChatLine['from'], text: string, tsMs: number) => {
      const line: ChatLine = { id: `${tsMs}-${Math.random().toString(36).slice(2, 8)}`, from, text, tsMs }
      patch((s) => ({ chat: applyChatLine(s.chat, line, 'remote'), unreadChat: chatOpenRef.current || from === 'operator' ? s.unreadChat : s.unreadChat + 1 }))
      if (from === 'device') optionsRef.current.onChatNotify?.(line, chatOpenRef.current)
    },
    [patch],
  )

  /** Test/dev hook: inject a device chat line as if it arrived on the control channel. */
  const debugPushDeviceChat = useCallback((text: string) => receiveChat('device', text, Date.now()), [receiveChat])

  const clearTimers = () => {
    for (const t of timers.current) clearTimeout(t)
    timers.current = []
    if (statsTimer.current) clearInterval(statsTimer.current)
    statsTimer.current = null
  }

  /** Mirror of `state.privacyScreen` for the control-channel reducer (side effects stay out of setState). */
  const privacyScreenRef = useRef<PrivacyScreenState>(initialPrivacyScreen)

  const teardown = useCallback(() => {
    clearTimers()
    unsubscribe.current?.()
    unsubscribe.current = null
    transferManager.detach()
    // The privacy screen never outlives the session (the agent drops it on disconnect).
    privacyScreenRef.current = initialPrivacyScreen
    patch({ privacyScreen: initialPrivacyScreen })
    inputRef.current?.close()
    fastInputRef.current?.close()
    controlRef.current?.close()
    filesRef.current?.close()
    inputRef.current = null
    fastInputRef.current = null
    fastStateRef.current = 'closed'
    controlRef.current = null
    filesRef.current = null
    cursorStore.reset()
    pcRef.current?.close()
    pcRef.current = null
    remoteSet.current = false
    pendingCandidates.current = []
  }, [cursorStore, patch])

  const fail = useCallback(
    (code: string, message?: string) => {
      teardown()
      patch({ phase: 'error', error: { code, message: message ?? ERROR_TEXT[code] ?? 'Something went wrong.' } })
    },
    [teardown, patch],
  )

  const sendControl = useCallback((msg: ControlMessage) => {
    const ch = controlRef.current
    if (!ch || ch.readyState !== 'open') return false
    ch.send(JSON.stringify(msg, (_k, v) => (typeof v === 'bigint' ? Number(v) : v)))
    return true
  }, [])

  const controlPausedRef = useRef(false)
  const sendInput = useCallback((ev: InputEvent) => {
    const ch = inputRef.current
    if (!ch || ch.readyState !== 'open') return false
    // While the device user has paused control only key releases may go through.
    if (controlPausedRef.current && ev.t !== 'rel') return false
    if (ev.t === 'mm') {
      // Pointer moves go over the unordered/unreliable channel when the agent opened it: a
      // lost move is superseded by the next one within a frame. Buttons and keys stay reliable.
      const route = moveChannel(fastStateRef.current, performance.now() - offerAtRef.current)
      if (route === 'fast') {
        const fast = fastInputRef.current
        if (fast && fast.readyState === 'open') {
          fast.send(JSON.stringify(ev))
          return true
        }
      } else if (route === 'drop') {
        return true
      }
    }
    ch.send(JSON.stringify(ev))
    return true
  }, [])

  const end = useCallback(
    (reason: EndReason = 'operator_closed') => {
      const id = sessionIdRef.current
      sendInput({ t: 'rel' })
      if (id) uiSocket.send({ type: 'session_end', session_id: id })
      teardown()
      patch({ phase: 'ended', endReason: reason })
    },
    [sendInput, teardown, patch],
  )

  const handleSessionState = useCallback(
    (s: SessionState, reason?: EndReason | null) => {
      if (s === 'awaiting_approval') patch({ phase: 'awaiting_approval' })
      else if (s === 'connecting' && phaseRef.current === 'awaiting_approval') patch({ phase: 'connecting' })
      else if (s === 'ended') {
        const r = reason ?? 'error'
        if (r === 'denied' || r === 'approval_timeout' || r === 'agent_offline' || r === 'connection_failed' || r === 'error') {
          fail(r === 'agent_offline' ? 'device_offline' : r)
        } else {
          teardown()
          patch({ phase: 'ended', endReason: r })
        }
      }
    },
    [patch, fail, teardown],
  )

  const handleControl = useCallback(
    (msg: ControlMessage) => {
      switch (msg.t) {
        case 'display_info':
          patch({ displays: msg.displays, currentDisplay: msg.current, activeDisplays: msg.active ?? [], audioAvailable: !!msg.audio })
          break
        case 'stats':
          patch((s) => ({
            agentStats: {
              ...s.agentStats,
              [msg.display ?? 0]: {
                display: msg.display ?? 0,
                codec: msg.codec,
                fps: msg.fps,
                bitrate_kbps: msg.bitrate_kbps,
                width: msg.width,
                height: msg.height,
                pipeline_ms: msg.pipeline_ms,
                hardware: msg.hardware,
                encodedWidth: msg.encoded_width || msg.width,
                encodedHeight: msg.encoded_height || msg.height,
                captureToEncodedMs: msg.capture_to_encoded_ms ?? 0,
                encodeMs: msg.encode_ms ?? 0,
                keyframes: msg.keyframes ?? 0,
                framesSkippedIdle: msg.frames_skipped_idle ?? 0,
              },
            },
          }))
          break
        case 'cursor_shape':
          cursorStore.setShape({ id: msg.id, url: `data:image/png;base64,${msg.png_base64}`, hotspotX: msg.hotspot_x, hotspotY: msg.hotspot_y, width: msg.width, height: msg.height })
          break
        case 'cursor_position':
          cursorStore.setPosition({ display: msg.display, x: msg.x, y: msg.y, shapeId: msg.shape_id, visible: msg.visible })
          break
        case 'clipboard_changed':
          patch({ remoteClipboard: msg.text })
          navigator.clipboard?.writeText(msg.text).catch(() => {
            /* needs a user gesture in some browsers; the toolbar offers a copy button */
          })
          break
        case 'clipboard_available':
          patch({ remoteClipboardRich: { kind: msg.kind, names: msg.names, totalBytes: Number(msg.total_bytes), at: Date.now() } })
          break
        case 'chat':
          // Device lines arrive here on the control channel; the operator's own lines are echoed
          // locally by sendChat. The console's session_event stream later confirms both.
          receiveChat(msg.from, msg.text, Number(msg.ts_ms))
          break
        case 'session_ended_by_user':
          teardown()
          patch({ phase: 'ended', endReason: 'device_user_closed' })
          break
        case 'annotations_disabled':
          patch({ annotationsDisabled: true })
          break
        case 'control_paused': {
          const next = reduceControlPaused({ controlPaused: controlPausedRef.current }, msg.paused)
          if (next.notice === null) break
          controlPausedRef.current = next.controlPaused
          if (next.controlPaused) sendInput({ t: 'rel' })
          patch({ controlPaused: next.controlPaused })
          break
        }
        case 'privacy_screen':
        case 'privacy_screen_denied': {
          const next = reducePrivacyScreen(privacyScreenRef.current, msg)
          privacyScreenRef.current = next.state
          patch({ privacyScreen: next.state })
          if (next.notice) toast[next.notice.kind](next.notice.text)
          break
        }
        default: {
          // Newer control messages (e.g. observers joining) are surfaced generically.
          const m = msg as { t: string; name?: string }
          if (m.t === 'observer_joined' && m.name) patch((s) => ({ observers: [...s.observers.filter((n) => n !== m.name), m.name!] }))
          if (m.t === 'observer_left' && m.name) patch((s) => ({ observers: s.observers.filter((n) => n !== m.name) }))
          break
        }
      }
    },
    [patch, teardown, receiveChat, sendInput, cursorStore],
  )

  // Dev/test hook: inject a control-channel message as if the agent had sent it.
  const debugPushControl = useCallback((msg: ControlMessage) => handleControl(msg), [handleControl])

  // Dev/test hook: show a synthetic picture for `display` (a canvas capture stream) so the
  // viewer can be exercised without a device — e.g. to draw annotations in a UI test.
  const debugFakeStream = useCallback(
    (display: number, width = 1920, height = 1080) => {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const g = ctx.createLinearGradient(0, 0, width, height)
        g.addColorStop(0, '#1e293b')
        g.addColorStop(1, '#0f172a')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, width, height)
        ctx.fillStyle = '#334155'
        for (let x = 0; x < width; x += 160) ctx.fillRect(x, 0, 1, height)
        for (let y = 0; y < height; y += 160) ctx.fillRect(0, y, width, 1)
      }
      const stream = (canvas as HTMLCanvasElement & { captureStream: (fps?: number) => MediaStream }).captureStream(5)
      patch((s) => ({
        phase: 'connected',
        streams: { ...s.streams, [display]: stream },
        displays: s.displays.length ? s.displays : [{ index: display, name: 'Fake display', x: 0, y: 0, width, height, scale: 1, primary: true }],
        activeDisplays: s.activeDisplays.includes(display) ? s.activeDisplays : [...s.activeDisplays, display],
      }))
    },
    [patch],
  )

  const start = useCallback(async () => {
    teardown()
    sessionIdRef.current = null
    controlPausedRef.current = false
    patch({ ...initial, phase: 'connecting' })

    if (uiSocket.status !== 'open') {
      fail('ws_closed')
      return
    }

    const pc = new RTCPeerConnection({ iceCandidatePoolSize: 0 })
    pcRef.current = pc

    // One recvonly video transceiver per display, in DisplayInfo index order; the agent
    // binds the i-th video m-line to display i.
    const known = [...optionsRef.current.knownDisplays].sort((a, b) => a.index - b.index)
    const videoTransceivers: RTCRtpTransceiver[] = []
    let requestedCodec: 'h265' | 'h264' | 'unknown' = 'unknown'
    for (let i = 0; i < videoTransceiverCount(known); i++) {
      const tr = pc.addTransceiver('video', { direction: 'recvonly' })
      requestedCodec = applyCodecPreferences(tr)
      videoTransceivers.push(tr)
    }
    const bindings = mapVideoTransceivers(known, videoTransceivers)
    const audioTransceiver = pc.addTransceiver('audio', { direction: 'recvonly' })
    patch({ requestedCodec, displays: known, currentDisplay: primaryDisplay(known), activeDisplays: known.length ? [primaryDisplay(known)] : [0] })

    // Data channels are created by the browser (the offerer).
    const input = pc.createDataChannel('input', { ordered: true })
    const control = pc.createDataChannel('control', { ordered: true })
    const files = pc.createDataChannel('files', { ordered: true })
    // Unordered + unreliable: pointer moves only (see sendInput). Older agents ignore it.
    const fast = pc.createDataChannel('input-fast', { ordered: false, maxRetransmits: 0 })
    inputRef.current = input
    controlRef.current = control
    filesRef.current = files
    fastInputRef.current = fast
    fastStateRef.current = 'connecting'
    fast.onopen = () => {
      fastStateRef.current = 'open'
      patch({ fastInput: 'open' })
    }
    fast.onclose = () => {
      fastStateRef.current = 'closed'
      patch({ fastInput: 'closed' })
    }
    fast.onerror = () => {
      fastStateRef.current = 'closed'
      patch({ fastInput: 'closed' })
    }
    // The agent may send control messages as text or binary frames (Chromium delivers binary as
    // a Blob by default); decode all three shapes before parsing.
    control.binaryType = 'arraybuffer'
    control.onmessage = (ev) => {
      const data: unknown = ev.data
      const dispatch = (text: string) => {
        try {
          handleControl(JSON.parse(text) as ControlMessage)
        } catch {
          /* ignore malformed */
        }
      }
      if (typeof data === 'string') dispatch(data)
      else if (data instanceof ArrayBuffer) dispatch(new TextDecoder().decode(data))
      else if (data instanceof Blob) void data.text().then(dispatch)
    }
    files.onopen = () => {
      transferManager.deviceId = deviceId
      transferManager.attach(new RtcFilesChannel(files))
    }

    pc.ontrack = (ev) => {
      // Lowest-latency playout: no adaptive jitter buffer growth for our stream.
      const receiver = ev.receiver as RTCRtpReceiver & { playoutDelayHint?: number; jitterBufferTarget?: number | null }
      try {
        if ('playoutDelayHint' in receiver) receiver.playoutDelayHint = 0
        if ('jitterBufferTarget' in receiver) receiver.jitterBufferTarget = 0
      } catch {
        /* not supported by this browser */
      }
      const stream = ev.streams[0] ?? new MediaStream([ev.track])
      if (ev.track.kind === 'audio' || ev.transceiver === audioTransceiver) {
        patch({ audioStream: stream })
        return
      }
      const b = bindings.find((x) => x.transceiver === ev.transceiver) ?? bindings.find((x) => x.transceiver.mid === ev.transceiver.mid)
      const display = b ? b.display : Number(ev.transceiver.mid ?? 0)
      patch((s) => ({ streams: { ...s.streams, [display]: stream } }))
    }
    pc.oniceconnectionstatechange = () => {
      patch({ iceState: pc.iceConnectionState })
    }
    // A browser whose WebRTC UDP is disabled (VPN / privacy extension policy) finishes
    // gathering without a single candidate; it would otherwise spin until the ICE timeout.
    let localCandidates = 0
    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === 'complete' && localCandidates === 0 && pc.connectionState !== 'connected') {
        fail('no_ice_candidates')
      }
    }
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        patch({ phase: 'connected' })
        clearTimers()
        let prev: { bytes: number; at: number } | undefined
        statsTimer.current = setInterval(async () => {
          if (!pcRef.current) return
          try {
            const r = await readStats(pcRef.current, prev)
            prev = r.sample
            patch({ rtcStats: r.snapshot })
          } catch {
            /* stats unavailable */
          }
        }, 1000)
      } else if (pc.connectionState === 'failed') {
        fail('connection_failed')
      }
    }

    // ICE candidates go out as soon as we have a session id (gathering only starts after
    // setLocalDescription, which happens once `session_created` arrived).
    pc.onicecandidate = (ev) => {
      if (ev.candidate) localCandidates += 1
      if (!ev.candidate || !sessionIdRef.current) return
      uiSocket.send({ type: 'ice_candidate', session_id: sessionIdRef.current, candidate: fromRtcCandidate(ev.candidate) })
    }

    let offer: RTCSessionDescriptionInit
    try {
      offer = await pc.createOffer()
      offerAtRef.current = performance.now()
    } catch (e) {
      fail('agent_error', `Could not create an offer: ${(e as Error).message}`)
      return
    }
    if (pcRef.current !== pc) return

    // Wait for the console to accept the offer, then start ICE with the session's servers.
    const created = new Promise<{ session_id: string; ice_servers: IceServer[] }>((resolve, reject) => {
      const off = uiSocket.onMessage((m: ConsoleToUi) => {
        if (m.type === 'session_created' && m.device_id === deviceId && !sessionIdRef.current) {
          off()
          resolve({ session_id: m.session_id, ice_servers: m.ice_servers })
        } else if (m.type === 'error' && !m.session_id && !sessionIdRef.current) {
          off()
          reject(new SessionError(m.code, m.message))
        }
      })
      timers.current.push(
        setTimeout(() => {
          off()
          reject(new SessionError('timeout'))
        }, CREATE_TIMEOUT_MS),
      )
    })

    if (!uiSocket.send({ type: 'session_offer', device_id: deviceId, offer: { type: 'offer', sdp: offer.sdp ?? '' } })) {
      fail('ws_closed')
      return
    }

    let sessionId: string
    let iceServers: IceServer[]
    try {
      ;({ session_id: sessionId, ice_servers: iceServers } = await created)
    } catch (e) {
      const se = e as SessionError
      fail(se.code, ERROR_TEXT[se.code] ?? se.message)
      return
    }
    if (pcRef.current !== pc) return
    sessionIdRef.current = sessionId
    patch({ sessionId })

    // Everything for this session from now on.
    unsubscribe.current = uiSocket.onSession(sessionId, async (m) => {
      if (pcRef.current !== pc) return
      switch (m.type) {
        case 'session_answer': {
          patch({ codec: m.codec })
          try {
            await pc.setRemoteDescription({ type: 'answer', sdp: m.answer.sdp })
            remoteSet.current = true
            for (const c of pendingCandidates.current) await pc.addIceCandidate(c).catch(() => undefined)
            pendingCandidates.current = []
          } catch (e) {
            fail('agent_error', `Could not apply the answer: ${(e as Error).message}`)
          }
          break
        }
        case 'ice_candidate': {
          const c = toRtcCandidate(m.candidate)
          if (remoteSet.current) await pc.addIceCandidate(c).catch(() => undefined)
          else pendingCandidates.current.push(c)
          break
        }
        case 'session_update':
          handleSessionState(m.session.state, m.session.end_reason ?? null)
          break
        case 'error':
          fail(m.code, ERROR_TEXT[m.code] ?? m.message)
          break
        default:
          break
      }
    })

    try {
      pc.setConfiguration({ iceServers: toRtcIceServers(iceServers), iceCandidatePoolSize: 0 })
      await pc.setLocalDescription(offer)
    } catch (e) {
      fail('agent_error', `Could not start ICE: ${(e as Error).message}`)
      return
    }

    timers.current.push(
      setTimeout(() => {
        if (pcRef.current === pc && phaseRef.current !== 'connected' && phaseRef.current !== 'awaiting_approval') {
          fail('connection_failed')
        }
      }, CONNECT_TIMEOUT_MS),
    )
  }, [deviceId, teardown, patch, fail, handleControl, handleSessionState])

  // End the session when the socket dies or the page goes away.
  useEffect(() => {
    const off = uiSocket.onStatus((s) => {
      if (s === 'closed' && pcRef.current) fail('ws_closed')
    })
    const onUnload = () => {
      const id = sessionIdRef.current
      if (id) uiSocket.send({ type: 'session_end', session_id: id })
    }
    window.addEventListener('pagehide', onUnload)
    return () => {
      off()
      window.removeEventListener('pagehide', onUnload)
      onUnload()
      teardown()
    }
  }, [fail, teardown])

  const selectDisplay = useCallback(
    (index: number) => {
      if (sendControl({ t: 'select_display', index })) patch({ currentDisplay: index })
    },
    [sendControl, patch],
  )

  const setActiveDisplays = useCallback(
    (indices: number[]) => {
      const uniq = Array.from(new Set(indices)).sort((a, b) => a - b)
      if (uniq.length === 0) return
      if (sendControl({ t: 'set_active_displays', indices: uniq })) patch({ activeDisplays: uniq })
    },
    [sendControl, patch],
  )

  /** Tell the agent the size at which `display` is rendered (null = full resolution). */
  const setViewport = useCallback(
    (display: number, width: number | null, height: number | null) =>
      sendControl({ t: 'set_viewport', display, width: width ?? undefined, height: height ?? undefined }),
    [sendControl],
  )

  const setAudio = useCallback(
    (enabled: boolean) => {
      if (sendControl({ t: 'set_audio', enabled })) patch({ audioEnabled: enabled })
    },
    [sendControl, patch],
  )

  /**
   * Ask the agent to hide / show the device's displays. No optimistic state: the button follows
   * the agent's `privacy_screen` echo, and a refusal arrives as `privacy_screen_denied`.
   */
  const setPrivacyScreen = useCallback((enabled: boolean) => sendControl({ t: 'set_privacy_screen', enabled }), [sendControl])

  const sendChat = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return false
      const tsMs = Date.now()
      if (!sendControl({ t: 'chat', from: 'operator', text: trimmed, ts_ms: BigInt(tsMs) })) return false
      patch((s) => ({ chat: applyChatLine(s.chat, { id: `${tsMs}-op`, from: 'operator', text: trimmed, tsMs }, 'local') }))
      return true
    },
    [sendControl, patch],
  )

  /** Called by the chat drawer so incoming lines don't count as unread while it is open. */
  const setChatOpen = useCallback(
    (open: boolean) => {
      chatOpenRef.current = open
      if (open) patch({ unreadChat: 0 })
    },
    [patch],
  )

  /** Seed the transcript from persisted session events (reconnect to the same session). */
  const seedChat = useCallback(
    (lines: ChatLine[]) => {
      patch((s) => {
        const chat = mergeChatSeed(s.chat, lines)
        return chat === s.chat ? {} : { chat }
      })
    },
    [patch],
  )

  const clearRichClipboard = useCallback(() => patch({ remoteClipboardRich: null }), [patch])

  /** Cumulative inbound video counters for the latency rig (bytes + decoded frames). */
  const readRawStats = useCallback(async () => {
    const pc = pcRef.current
    if (!pc) return null
    const report = await pc.getStats()
    let bytes = 0
    let framesDecoded = 0
    report.forEach((r) => {
      if (r.type === 'inbound-rtp' && (r as { kind?: string }).kind === 'video') {
        bytes = (r as { bytesReceived?: number }).bytesReceived ?? 0
        framesDecoded = (r as { framesDecoded?: number }).framesDecoded ?? 0
      }
    })
    return { bytes, framesDecoded }
  }, [])

  return { state, start, end, sendInput, sendControl, selectDisplay, setActiveDisplays, setAudio, setPrivacyScreen, setViewport, sendChat, setChatOpen, seedChat, clearRichClipboard, cursorStore, readRawStats, debugPushDeviceChat, debugPushControl, debugFakeStream }
}

class SessionError extends Error {
  code: string
  constructor(code: string, message?: string) {
    super(message ?? code)
    this.code = code
  }
}
